import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link,
  Spinner,
  Switch,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { FiTrash } from 'react-icons/all';
import { Stack } from '@chakra-ui/layout';
import { Heading, Pagination, Table } from '~/components';
import { ROUTER_PATHS } from '~/constants';
import { useDashboards } from '~/hooks/useDashboards';
import { queryClient } from '~/utils/react-query';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Dashboard } from '~/types';
import { DashboardForm } from '~/pages/Portal/Admin/Dashboards/DashboardForm';
import { atualizaExibirRelatorio, deleteDashboard } from '~/services/Dashboard';

export const Dashboards: React.VFC = () => {
  const history = useHistory();
  const toast = useToast();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [page, setPage] = useState(1);
  const [listaDashboards, setlistaDashboards] = useState<Dashboard[]>([]);
  const [dashboardSelecionado, setDashboardSelecionado] = useState<
    Dashboard | undefined
  >(undefined);
  const [isFetchingRemover, setIsFetchingRemover] = useState(false);
  const [
    isFetchingUpdateExibirRelatorio,
    setIsFetchingAtualizaExibirRelatorio,
  ] = useState<boolean>(false);
  const {
    data,
    isLoading,
    error,
    refetch: refetchDashboard,
  } = useDashboards(page, 10, {
    onSuccess: r => {
      setlistaDashboards(r?.results || []);
    },
  });
  useEffect(() => {
    refetchDashboard();
  }, []);
  const removerDashboard = (id: number) => {
    setIsFetchingRemover(true);
    deleteDashboard(id!)
      .then(() => {
        toast({
          title: 'URL excluída com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        onCloseModal();
        if (listaDashboards.length === 1 && page > 1) {
          setPage(page - 1);
          queryClient.invalidateQueries(['dashboards', page - 1]);
        } else {
          queryClient.invalidateQueries(['dashboards', page]);
        }
      })
      .catch(() => {
        toast({
          title: 'Erro ao excluir URL',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsFetchingRemover(false));
  };
  const atualizarExibirRelatorio = async (id: number, value: boolean) => {
    setIsFetchingAtualizaExibirRelatorio(true);
    await atualizaExibirRelatorio(id, value)
      .then(() => refetchDashboard())
      .catch(err => {
        toast({
          title: 'Erro ao atualizar Exibir relatório',
          description: err.response?.data?.detail || '',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingAtualizaExibirRelatorio(false);
      });
  };
  return (
    <Box px="8" py="4">
      <Stack gridGap={4}>
        <HStack justifyContent="space-between">
          <Heading>Cadastro de Dashboards</Heading>
          <DashboardForm isEdit={false} />
        </HStack>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos dashboards.</Text>
          </Flex>
        ) : (
          <>
            <TableContainer>
              <Table type="secondary">
                <Thead>
                  <Tr>
                    <Th w="70%" maxW="70%" style={{ textTransform: 'none' }}>
                      Nome do relatório
                    </Th>
                    <Th w="10%" maxW="10%">
                      API
                    </Th>
                    <Th
                      w="10%"
                      maxW="10%"
                      style={{ textTransform: 'none' }}
                      textAlign="center"
                    >
                      Exibir relatório
                    </Th>
                    <Th w="10%" maxW="10%" textAlign="center">
                      Ações
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {listaDashboards.length ? (
                    listaDashboards.map((dashboard, index) => (
                      <Tr key={dashboard.id}>
                        <Td whiteSpace="normal">
                          {dashboard.nome_relatorio || '-'}
                        </Td>
                        <Td>
                          <Link
                            isExternal
                            m="0px !important"
                            fontWeight="500"
                            _hover={{ textDecoration: 'none' }}
                            textDecoration="none"
                            color="#3b61f9"
                            href={ROUTER_PATHS.PORTAL.POWERBI_REPORT.replace(
                              ':id',
                              dashboard.id.toString(),
                            )}
                          >
                            Link
                          </Link>
                        </Td>
                        <Td textAlign="center">
                          <Stack position="relative">
                            <Switch
                              id="status"
                              size="md"
                              isDisabled={isFetchingUpdateExibirRelatorio}
                              colorScheme="green"
                              isChecked={dashboard.exibir_relatorio}
                              onChange={e => {
                                atualizarExibirRelatorio(
                                  dashboard.id,
                                  e.target.checked,
                                );
                              }}
                            />
                          </Stack>
                        </Td>
                        <Td textAlign="center">
                          <DashboardForm isEdit dashboard={dashboard} />
                          <Tooltip
                            hasArrow
                            label="Remover dashboard"
                            bg="black"
                          >
                            <IconButton
                              size="sm"
                              aria-label="Remover dashboard"
                              icon={<Icon as={FiTrash} />}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDashboardSelecionado(dashboard);
                                onOpenModal();
                              }}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={7} textAlign="center">
                        {isLoading && <Spinner />}
                        {!isLoading && <Text>Nenhuma URL cadastrada</Text>}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
            <Flex justify="flex-end" height="fit-content">
              {data && (
                <Pagination
                  marginTop="0px"
                  totalCountOfRegisters={data.count}
                  registersPerPage={10}
                  currentPage={page}
                  onPageChange={e => {
                    setPage(e);
                    queryClient.invalidateQueries(['dashboards', e]);
                  }}
                />
              )}
            </Flex>
          </>
        )}
      </Stack>
      <ConfirmationDialog
        confirmar={() => removerDashboard(dashboardSelecionado!.id)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Remover URL"
        btnConfirmText="Confirmar"
        isDisabled={isFetchingRemover}
      >
        <span>Tem certeza que deseja remover a URL?</span>
      </ConfirmationDialog>
    </Box>
  );
};
